import { Context, createContext, ReactNode, useContext, useState } from 'react';

// Define the type for our context value
type TabsContextType = {
  activeTab: number;
  setActiveTab: (value: number) => void;
};

// Create a map to store multiple contexts
const tabsContextMap = new Map<string, Context<TabsContextType>>();

// Default context
export const DefaultTabsContext = createContext<TabsContextType>({
  activeTab: 0,
  setActiveTab: (_: number) => {},
});

// Function to get or create a context for a specific ID
export const getTabsContext = (contextId = 'default'): Context<TabsContextType> => {
  if (!tabsContextMap.has(contextId)) {
    tabsContextMap.set(
      contextId,
      createContext<TabsContextType>({
        activeTab: 0,
        setActiveTab: (_: number) => {},
      }),
    );
  }
  return tabsContextMap.get(contextId)!;
};

// Hook to use the tabs context with a specific ID
export const useTabsContext = (contextId = 'default'): TabsContextType => {
  const context = contextId === 'default' ? DefaultTabsContext : getTabsContext(contextId);
  return useContext(context);
};

type Props = {
  children: ReactNode;
  defaultTab?: number;
  contextId?: string;
};

const Tabs = ({ children, defaultTab = 0, contextId = 'default' }: Props) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  const TabsContext = contextId === 'default' ? DefaultTabsContext : getTabsContext(contextId);

  return (
    <TabsContext.Provider value={{ activeTab, setActiveTab }}>
      <div className="mx-auto">{children}</div>
    </TabsContext.Provider>
  );
};

export default Tabs;
